import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  DatoGeograficoCatalogoDto,
  EjecutivoCementoDto,
  EjecutivoCementoUpsertDto,
} from '../models/ejecutivo-cemento.dto';
import { ServerPaginatedResponse } from '../models/server-list.model';

export interface EjecutivoCementoListQuery {
  page: number;
  pageSize: number;
  search: string;
  ordering: string;
  activo: 'all' | 'true' | 'false';
}

@Injectable()
export class EjecutivoCementoApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}cemento/admin-executives/`;
  private readonly geographicDataUrl = `${environment.apiUrl}cemento/admin-geographic-data/`;

  listItems(query: EjecutivoCementoListQuery): Observable<ServerPaginatedResponse<EjecutivoCementoDto>> {
    const params = new HttpParams()
      .set('page', String(query.page))
      .set('page_size', String(query.pageSize))
      .set('search', query.search)
      .set('ordering', query.ordering)
      .set('activo', query.activo);
    return this.http.get<ServerPaginatedResponse<EjecutivoCementoDto>>(this.baseUrl, { params });
  }

  createItem(payload: EjecutivoCementoUpsertDto): Observable<void> {
    return this.http.post(this.baseUrl, payload).pipe(map(() => undefined));
  }

  updateItem(id: number, payload: EjecutivoCementoUpsertDto): Observable<void> {
    return this.http.put(`${this.baseUrl}${id}/`, payload).pipe(map(() => undefined));
  }

  deactivateItem(id: number): Observable<void> {
    return this.http.delete(`${this.baseUrl}${id}/`).pipe(map(() => undefined));
  }

  loadGeographicCatalog(): Observable<DatoGeograficoCatalogoDto[]> {
    return this.listGeoPage(1).pipe(
      switchMap((firstPage) => {
        const totalPages = Math.max(1, Math.ceil(firstPage.count / 100));
        if (totalPages === 1) {
          return of(firstPage.results);
        }
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
        return forkJoin(remainingPages.map((page) => this.listGeoPage(page))).pipe(
          map((pages) => [firstPage, ...pages].flatMap((page) => page.results)),
        );
      }),
    );
  }

  private listGeoPage(page: number): Observable<ServerPaginatedResponse<DatoGeograficoCatalogoDto>> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('page_size', '100')
      .set('search', '')
      .set('ordering', 'codigoRegion');
    return this.http.get<ServerPaginatedResponse<DatoGeograficoCatalogoDto>>(this.geographicDataUrl, { params });
  }
}
